import get from 'lodash/get';
import includes from 'lodash/includes';

import { TransferConfig } from '../../config/transfer-config';

import TransferError from '@app/helper/error';
import TransferErrorMessages from '@app/helper/error/messages';

import retriableRequest from '@module/http/axios/retriable-request';
import request from '@app/modules/http/axios/request';
import { HttpResponseCodes } from '@module/http/axios/response';

function retryApiRequest(method, path, params = null, options = {}) {
  const retries = get(options, 'retries.limit', TransferConfig.api.retry.limit);

  return retriableRequest({
    path: `${TransferConfig.api.endpoint}${path}`,
    method,
    retries,
    timeout: TransferConfig.api.timeout,
    params,
  });
}

export async function fetchFileObjectId(transferId, params) {
  try {
    const response = await retryApiRequest(
      'POST',
      `/transfers/${transferId}/files`,
      params,
      {
        retries: {
          limit: TransferConfig.upload.retry.limit,
        },
      }
    );
    return response.body.id;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.INVALID_REQUEST);
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function fetchPartPutUrl(transferId, fileObjectId, params) {
  try {
    const response = await retryApiRequest(
      'POST',
      `/transfers/${transferId}/files/${fileObjectId}/part-put-url`,
      params
    );
    return response.body;
  } catch (error) {
    if (includes(error.message, 'timeout')) {
      throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }

    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function finalizeFileObject(transferId, fileObjectId, params) {
  try {
    const response = await retryApiRequest(
      'PUT',
      `/transfers/${transferId}/files/${fileObjectId}/finalize-mpp`,
      params
    );
    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      case HttpResponseCodes.EXPECTATION_FAILED:
        if (error.body && error.body['missing_chunks']) return error.body;
        break;
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function verifyToken(transferId, params) {
  try {
    return await request({
      method: 'POST',
      path: `${TransferConfig.api.endpoint}/transfers/${transferId}/verify`,
      params,
    });
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.TOO_MANY_REQUESTS:
        throw error;
      default:
        throw new TransferError(TransferErrorMessages.WRONG_VERIFICATION_CODE);
    }
  }
}

export async function resendToken(transferId, params) {
  return await request({
    method: 'POST',
    path: `${TransferConfig.api.endpoint}/transfers/${transferId}/resend-verification-code`,
    params,
  });
}

export async function fetchTransfer(
  transferId,
  secret,
  recipientId = false,
  password = false,
  retry = true
) {
  const requestPath = `/transfers/${transferId}/prepare-download`;
  const options = {};
  const params = {
    security_hash: secret,
  };

  if (recipientId) {
    params.recipient_id = recipientId;
  }

  if (password) {
    params.password = password;
  }

  if (retry === false) {
    options.retries = 1;
  }

  try {
    const response = await retryApiRequest(
      'POST',
      requestPath,
      params,
      options
    );
    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function requestPreview(
  transferId,
  secret,
  recipientId = false,
  transferItemId = false,
  password = false,
  geometry
) {
  let initialPath = `/transfers/${transferId}`;
  const params = {
    security_hash: secret,
  };

  // Do we want to fetch a download for single item or entire transfer?
  if (transferItemId !== false) {
    initialPath = `${initialPath}/files/${transferItemId}`;
  }
  initialPath = `${initialPath}/preview`;

  if (geometry && /\d+x\d+/g.test(geometry)) {
    initialPath = `${initialPath}?geometry=${geometry}`;
  }

  if (recipientId) {
    params.recipient_id = recipientId;
  }

  if (password) {
    params.password = password;
  }

  try {
    const response = await retryApiRequest('POST', initialPath, params);
    return response.body;
  } catch (e) {
    throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
  }
}

export async function requestPreviews({
  transferId,
  securityHash,
  height,
  password,
}) {
  const path = `/transfers/${transferId}/previews`;

  try {
    const params = {
      height,
      password,
      security_hash: securityHash,
    };

    const response = await retryApiRequest('POST', path, params);

    return response.body.map((preview) => ({
      fileId: preview.file_id,
      previewUrl: preview.preview_url,
    }));
  } catch (error) {
    throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
  }
}

export async function fetchTransferDownloadLink({
  transferId,
  secret,
  recipientId = false,
  fileIds = false,
  password = false,
  domainUserId = false,
  intent = 'entire_transfer',
}) {
  const path = `/transfers/${transferId}/download`;
  const params = {
    security_hash: secret,
    intent,
  };

  if (recipientId) {
    params.recipient_id = recipientId;
  }

  if (password) {
    params.password = password;
  }

  if (fileIds) {
    params.file_ids = fileIds;
  }

  if (domainUserId) {
    params.domain_user_id = domainUserId;
  }

  try {
    const response = await retryApiRequest('POST', path, params);
    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.TOO_MANY_REQUESTS:
        throw new TransferError(TransferErrorMessages.TOO_MANY_REQUESTS);
      default:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
    }
  }
}

export async function exitTransfer(transferId) {
  const response = await retryApiRequest(
    'PUT',
    `/transfers/${transferId}/browser_exit`
  );
  return response.body;
}

export async function cancelTransfer(transferId) {
  const response = await retryApiRequest('DELETE', `/transfers/${transferId}`);
  return response.body;
}

export async function validateRecaptcha(transferId, params) {
  try {
    const response = await retryApiRequest(
      'POST',
      `/transfers/${transferId}/human_verification`,
      params
    );
    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.RECAPTCHA_FAILED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function createTransferWithType(type, params) {
  try {
    const response = await retryApiRequest(
      'POST',
      `/transfers/${type}`,
      params
    );
    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.LIMIT_REACHED:
        throw new TransferError(TransferErrorMessages.LIMIT_REACHED);
      case HttpResponseCodes.USER_BLOCKED:
        throw new TransferError(TransferErrorMessages.USER_BLOCKED);
      case HttpResponseCodes.UNAVAILABLE_PARENT:
        throw new TransferError(TransferErrorMessages.UNAVAILABLE_PARENT);
      case HttpResponseCodes.BAD_REQUEST:
        if (get(error, 'response.data.message') === 'too_many_recipients') {
          throw new TransferError(TransferErrorMessages.TOO_MANY_RECIPIENTS);
        }
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      case HttpResponseCodes.EXPECTATION_FAILED:
        throw new TransferError(
          TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED,
          {
            transferId: error.body.id,
          }
        );
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function finalizeTransfer(transferId) {
  try {
    const response = await retryApiRequest(
      'PUT',
      `/transfers/${transferId}/finalize`
    );
    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export const TransferAPI = {
  fetchFileObjectId,
  fetchPartPutUrl,
  finalizeFileObject,
  verifyToken,
  resendToken,
  fetchTransfer,
  requestPreview,
  fetchTransferDownloadLink,
  exitTransfer,
  cancelTransfer,
  validateRecaptcha,
  createTransferWithType,
  requestPreviews,
};
